import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { useHistory, useParams } from 'react-router-dom';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import { ScreenPrompt } from 'components/ScreenPrompt';
import FlexSelect from 'components/Inputs/FlexSelect';
import { useSelector } from 'react-redux';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody,
  Button
} from 'reactstrap';
import FormTooltip from 'components/Inputs/FormTooltip';
import CreatableSelect from 'react-select/creatable';

import IgnoredWordsService from '../service/index';
import FlexChatIntegration from 'views/ServicePanel/components/QueueContent/components/Contact/service/FlexChatIntegration';

const IgnoredWordsForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const { reset, control, handleSubmit, errors, setValue, watch, formState } =
    useForm();
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const { isDirty, isSubmitting } = formState;

  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [canalOptions, setCanalOptions] = useState([]);
  const [canaisCarregados, setCanaisCarregados] = useState(false);

  const words = watch('words', []);

  useEffect(() => {
    loadClientChannels();
  }, []);

  useEffect(() => {
    if (id && canaisCarregados) {
      loadIgnoredWordData();
      setIsEdit(true);
    }
  }, [id, canaisCarregados]);

  const loadClientChannels = async () => {
    try {
      setLoading(true);
      const response = await FlexChatIntegration.getAllChannels();
      const info = response.data.data;

      const nomes = [
        'whatsapp',
        'webchat',
        'telegram',
        'instagram',
        'sms',
        'email',
        'facebook'
      ];
      let array = [];

      nomes.forEach((nome) => {
        if (!info[nome]) return;
        if (nome === 'whatsapp' || nome === 'webchat') {
          info[nome].forEach((item, index) => {
            array.push(info[nome][index] ?? []);
          });
          return;
        }
        array.push(info[nome] ?? []);
      });

      const list = array
        .filter((canal) => canal != null && canal.nome_canal)
        .map((canal) => ({
          label: canal.nome_canal,
          value: canal.id || canal.nome_canal
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setCanalOptions(list);
      setCanaisCarregados(true);
    } catch (error) {
      console.error('Erro ao carregar canais:', error);
      toast.error('Erro ao carregar canais disponíveis', {
        autoClose: 3000,
        closeOnClick: true
      });
    } finally {
      setLoading(false);
    }
  };

  const loadIgnoredWordData = async () => {
    setLoading(true);
    try {
      const response = await IgnoredWordsService.getById(id);
      const data = response.data.data || response.data;

      if (!data) {
        throw new Error('Nenhum dado retornado da API');
      }

      const channelIds = [];

      if (
        data.channel &&
        Array.isArray(data.channel) &&
        canalOptions.length > 0
      ) {
        data.channel.forEach((channelName) => {
          const channel = canalOptions.find(
            (opt) => opt.label.toLowerCase() === channelName.toLowerCase()
          );
          channelIds.push(channel ? channel.value : channelName);
        });
      } else {
        channelIds.push(...(data.channel || []));
      }

      const formData = {
        title: data.title || '',
        words: Array.isArray(data.words)
          ? data.words.map((word) => ({
              label: word.original,
              value: word.original
            }))
          : (data.words || '').split(',').map((word) => ({
              label: word.original.trim(),
              value: word.original.trim()
            })),
        channel: channelIds
      };
      reset(formData);
    } catch (err) {
      console.error('ERRO ao carregar dados para edição:', err);
      toast.error(
        'Erro ao carregar dados para edição! ' +
          (err.response?.data?.message || err.message),
        {
          autoClose: 5000,
          closeOnClick: true
        }
      );
      history.push('/admin/ignoredWords');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setIsLoadingData(true);
    try {
      const wordsArray = (
        Array.isArray(data.words)
          ? data.words.map((i) => i.value)
          : data.words.split(',')
      ).reduce((acc, word) => {
        const trimmed = word.trim();
        if (trimmed) acc.push({ original: trimmed, sanitized: null });
        return acc;
      }, []);

      const channelArray = data.channel.map((channelId) => {
        const channel = canalOptions.find((opt) => opt.value === channelId);
        return channel ? channel.label : channelId;
      });

      const submitData = {
        title: data.title.trim(),
        words: wordsArray,
        channel: channelArray
      };

      if (
        !submitData.title ||
        submitData.words.length === 0 ||
        submitData.channel.length === 0
      ) {
        toast.error('Preencha todos os campos obrigatórios!', {
          autoClose: 3000,
          closeOnClick: true
        });
        setIsLoadingData(false);
        return;
      }

      if (isEdit) {
        await IgnoredWordsService.update(id, submitData);
        toast.success('Grupo de palavras atualizado com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
      } else {
        await IgnoredWordsService.createIgnoredWords(submitData);
        toast.success('Grupo de palavras cadastrado com sucesso!', {
          autoClose: 3000,
          closeOnClick: true
        });
      }

      reset({
        title: data.title.trim(),
        words: wordsArray.map((word) => ({
          label: word.original,
          value: word.original
        })),
        channel: channelArray.map((channel) => ({
          label: channel,
          value: channel
        }))
      });
      setTimeout(() => {
        history.push('/admin/ignoredWords');
      });
    } catch (err) {
      console.error('Erro ao salvar grupo de palavras:', err);
      const errorMessage =
        err.response?.data?.message ||
        (isEdit
          ? 'Erro ao atualizar grupo de palavras!'
          : 'Erro ao criar grupo de palavras!');

      toast.error(errorMessage, {
        autoClose: 5000,
        closeOnClick: true
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.25rem' }}>
      {message}
    </p>
  );

  const cancelAction = () => {
    history.push('/admin/ignoredWords');
  };

  const validateWords = (value) => {
    const wordsArray = Array.isArray(value)
      ? value.map((item) => item.value.trim()).filter((word) => word)
      : value
          .split(',')
          .map((word) => word.trim())
          .filter((word) => word);

    if (wordsArray.length === 0) {
      return 'Pelo menos uma palavra é obrigatória!';
    }

    const invalidWords = wordsArray.filter(
      (word) => word.length < 2 || word.length > 50
    );
    if (invalidWords.length > 0) {
      return 'Cada palavra deve ter entre 2 e 50 caracteres';
    }

    const normalizedWords = wordsArray.map((word) =>
      word.normalize('NFC').toLowerCase()
    );
    const invalidChars = normalizedWords.filter(
      (word) => !/^[a-zÀ-ÿ0-9\s\-.,!?;:()'"@#$%&*+=]+$/.test(word)
    );
    if (invalidChars.length > 0) {
      return 'As palavras devem conter apenas letras (com ou sem acentos), números, espaços ou caracteres comuns de pontuação';
    }

    const uniqueWords = new Set(normalizedWords);
    if (uniqueWords.size !== normalizedWords.length) {
      return 'Não é permitido adicionar palavras duplicadas!';
    }

    return true;
  };

  if (loading && isEdit) {
    return (
      <Container className="mt--6" fluid>
        <Row className="justify-content-center">
          <ReactLoader />
        </Row>
      </Container>
    );
  }

  return (
    <>
      <ScreenPrompt
        when={isDirty && !isSubmitting}
        message="Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar"
      />
      <SimpleHeader
        name={
          isEdit
            ? 'Editar Grupo de Palavras Ignoradas'
            : 'Novo Grupo de Palavras Ignoradas'
        }
        parentName="Palavras Ignoradas"
        returnPath="/admin/ignoredWords"
        buttonTitle="Voltar para a página de palavras ignoradas"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">
                    {isEdit
                      ? 'Editar Grupo de Palavras Ignoradas'
                      : 'Novo Grupo de Palavras Ignoradas'}
                  </h3>
                </CardHeader>
                <CardBody>
                  <Form
                    className="needs-validation"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                  >
                    <Row className="mb-4">
                      <Col md="6">
                        <label className="form-control-label">Título *</label>
                        <Controller
                          as={Input}
                          control={control}
                          name="title"
                          rules={{
                            required: 'Título é obrigatório!',
                            minLength: {
                              value: 3,
                              message:
                                'O título deve ter pelo menos 3 caracteres'
                            },
                            maxLength: {
                              value: 100,
                              message:
                                'O título não pode ter mais de 100 caracteres'
                            }
                          }}
                          defaultValue=""
                          placeholder="Digite um título para o grupo de palavras ignoradas"
                          invalid={errors.title}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="title"
                          render={({ message }) => errorFormMessage(message)}
                        />
                      </Col>
                      <Col md="6">
                        <label className="form-control-label">
                          Canais *{' '}
                          <FormTooltip
                            text="Selecione os canais onde estas palavras serão
                            ignoradas"
                          />
                        </label>
                        <Controller
                          render={(props) => (
                            <FlexSelect
                              dataOptions={canalOptions}
                              isMulti={true}
                              isClearable={true}
                              value={props.value}
                              valueController={setValue}
                              closeMenuOnSelect={false}
                              fieldName="channel"
                              labelName="label"
                              valueName="value"
                              placeholder={
                                canalOptions.length > 0
                                  ? 'Selecione um ou mais canais'
                                  : 'Carregando canais...'
                              }
                              isDisabled={loading || canalOptions.length === 0}
                            />
                          )}
                          control={control}
                          rules={{
                            required: 'Selecione pelo menos um canal!',
                            validate: (value) =>
                              Array.isArray(value) && value.length > 0
                                ? true
                                : 'Selecione pelo menos um canal!'
                          }}
                          name="channel"
                          defaultValue={[]}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="channel"
                          render={({ message }) => errorFormMessage(message)}
                        />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md="6">
                        <label className="form-control-label">
                          Palavras *{' '}
                          <FormTooltip text="Digite as palavras que deseja ignorar e pressione Enter. Elas serão adicionadas à lista, impedindo que novas conversas sejam abertas para o canal associado após o encerramento da conversa." />
                        </label>
                        <Controller
                          render={(props) => (
                            <CreatableSelect
                              isMulti
                              isClearable={true}
                              options={[]}
                              value={props.value}
                              onChange={props.onChange}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const inputValue = e.target.value
                                    .trim()
                                    .toLowerCase();
                                  if (
                                    inputValue &&
                                    words.some(
                                      (word) =>
                                        word.value.toLowerCase() === inputValue
                                    )
                                  ) {
                                    e.preventDefault();
                                    toast.error(
                                      'Esta palavra já foi adicionada!',
                                      {
                                        autoClose: 3000,
                                        closeOnClick: true
                                      }
                                    );
                                  } else if (!inputValue) {
                                    e.preventDefault();
                                  }
                                }
                              }}
                              placeholder="Digite as palavras que deseja ignorar e pressione Enter"
                              labelName="label"
                              valueName="value"
                              formatCreateLabel={(inputValue) =>
                                `Adicionar "${inputValue}"`
                              }
                              components={{
                                DropdownIndicator: () => null,
                                IndicatorSeparator: () => null
                              }}
                            />
                          )}
                          control={control}
                          name="words"
                          rules={{
                            required: 'Pelo menos uma palavra é obrigatória!',
                            validate: validateWords
                          }}
                          defaultValue={[]}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="words"
                          render={({ message }) => errorFormMessage(message)}
                        />
                      </Col>
                    </Row>
                    <hr />
                    <div className="d-flex justify-content-end">
                      <Button
                        style={buttonStyle(systemConfiguration.primary_color)}
                        type="submit"
                        disabled={isLoadingData}
                        className="btn-primary"
                      >
                        {isLoadingData ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm mr-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            {isEdit ? 'Atualizando...' : 'Salvando...'}
                          </>
                        ) : isEdit ? (
                          'Atualizar'
                        ) : (
                          'Salvar'
                        )}
                      </Button>
                      <ReactButton
                        btnColor="cancelation"
                        onClick={cancelAction}
                        disabled={isLoadingData}
                        className="mr-2"
                      >
                        Cancelar
                      </ReactButton>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default IgnoredWordsForm;
